import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, LogOut } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useSignOut } from '../../../app/layout/UserMenus';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { fetchSession, SESSION_QUERY_KEY, useActiveSession } from '../../../lib/auth';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Field } from '../../../ui/Field';
import { Input, PasswordInput } from '../../../ui/Input';
import { Page } from '../../../ui/Page';
import { Section } from '../../../ui/Section';
import { toast } from '../../../ui/Toast';
import { PasswordChecklist } from '../../auth/components/PasswordChecklist';
import { passwordChecks, passwordProblem } from '../../auth/password-policy';

/**
 * Portal klienta — „Moje konto” (`/portal/konto`): imię i nazwisko (`portal.updateProfile`), email (login, tylko do
 * odczytu), zmiana hasła (`identity.changePassword`, ta sama polityka co w zespole) i wylogowanie. Bez ustawień
 * zespołu (powiadomienia, tokeny, integracje) — osoba klienta ich nie ma.
 */
export function PortalAccountPage() {
  const signOut = useSignOut();
  return (
    <Page width="reading" className="pb-16">
      <Link
        to="/portal"
        className="-my-2 inline-flex min-h-11 items-center gap-1.5 rounded-xs text-body-s font-medium text-fg-secondary hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
      >
        <ArrowLeft size={16} strokeWidth={1.75} aria-hidden />
        Wszystkie zgłoszenia
      </Link>
      <h1 className="mt-3 text-h3 text-fg">Moje konto</h1>
      <ProfileSection />
      <PasswordSection />
      <div className="mt-6">
        <Button variant="secondary" icon={LogOut} onClick={signOut} className="max-md:h-11">
          Wyloguj
        </Button>
      </div>
    </Page>
  );
}

function ProfileSection() {
  const session = useActiveSession();
  const queryClient = useQueryClient();
  const utils = trpc.useUtils();
  const me = trpc.portal.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const update = trpc.portal.updateProfile.useMutation();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const current = me.data?.person.name ?? session.user.name;

  useEffect(() => setName(current), [current]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (name.trim().length < 2) {
      setError('Podaj imię i nazwisko');
      return;
    }
    try {
      const next = await update.mutateAsync({ name: name.trim() });
      utils.portal.me.setData(undefined, next);
      await queryClient.fetchQuery({ queryKey: SESSION_QUERY_KEY, queryFn: fetchSession, staleTime: 0 });
      toast.success('Zapisano');
    } catch (err) {
      setError(validationIssues(err).find((i) => i.path === 'name')?.message ?? errorMessage(err));
    }
  };

  return (
    <Section className="mt-5" title="Dane" tone="strong">
      <form onSubmit={submit} noValidate className="flex max-w-[420px] flex-col gap-4" aria-label="Dane konta">
        <Field label="Imię i nazwisko" error={error}>
          <Input autoComplete="name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email" hint="Twój login. Zmianę adresu zrobi zespół — napisz w zgłoszeniu.">
          <Input type="email" value={session.user.email} readOnly disabled />
        </Field>
        <div>
          <Button
            type="submit"
            variant="primary"
            loading={update.isPending}
            disabled={name.trim() === current}
            className="max-md:h-11"
          >
            Zapisz
          </Button>
        </div>
      </form>
    </Section>
  );
}

type PasswordField = 'currentPassword' | 'newPassword' | 'confirm';

function PasswordSection() {
  const session = useActiveSession();
  const changePassword = trpc.identity.changePassword.useMutation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<PasswordField, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  const checks = useMemo(
    () => passwordChecks(next, { email: session.user.email, name: session.user.name, confirm }),
    [next, confirm, session.user.email, session.user.name],
  );
  const problem = passwordProblem(checks, next);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setFormError(null);
    const fieldErrors: Partial<Record<PasswordField, string>> = {};
    if (!current) fieldErrors.currentPassword = 'Podaj obecne hasło';
    if (problem === 'Hasła się różnią') fieldErrors.confirm = problem;
    else if (problem) fieldErrors.newPassword = problem;
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;
    try {
      await changePassword.mutateAsync({ currentPassword: current, newPassword: next });
      setCurrent('');
      setNext('');
      setConfirm('');
      setSubmitted(false);
      toast.success('Hasło zmienione', { description: 'Pozostałe urządzenia zostały wylogowane.' });
    } catch (err) {
      const mapped: Partial<Record<PasswordField, string>> = {};
      for (const issue of validationIssues(err)) {
        if (issue.path === 'currentPassword' || issue.path === 'newPassword') mapped[issue.path] = issue.message;
      }
      if (Object.keys(mapped).length > 0) {
        setErrors(mapped);
        return;
      }
      const code = appErrorCode(err);
      if (code === 'UNAUTHENTICATED' || code === 'FORBIDDEN') {
        setErrors({ currentPassword: 'Obecne hasło jest nieprawidłowe' });
        return;
      }
      if (code === 'STEP_UP_REQUIRED') return;
      setFormError(errorMessage(err));
    }
  };

  return (
    <Section className="mt-4" title="Hasło" tone="strong">
      <form
        ref={formRef}
        onSubmit={submit}
        noValidate
        className="flex max-w-[420px] flex-col gap-4"
        aria-label="Zmiana hasła"
      >
        <Field label="Obecne hasło" error={errors.currentPassword}>
          <PasswordInput autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="Nowe hasło" error={submitted ? errors.newPassword : null}>
          <PasswordInput autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="Powtórz nowe hasło" error={submitted ? errors.confirm : null}>
          <PasswordInput autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <PasswordChecklist checks={checks} />
        {formError && <FormMessage>{formError}</FormMessage>}
        <div>
          <Button type="submit" variant="primary" loading={changePassword.isPending} className="max-md:h-11">
            Zmień hasło
          </Button>
        </div>
      </form>
    </Section>
  );
}
